import {DeleteOutlined, PlusOutlined} from '@ant-design/icons'
import {Modal, Upload} from 'antd'
import type {UploadFile} from 'antd/es/upload/interface'
import {CreativeBean} from 'common/api/api.ts'
import {useAppContext} from 'common/context/AppContext.tsx'
import {env} from 'common/env'
import {showError} from 'common/util/Util.tsx'
import {forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState} from 'react'

import {getAspectRatioLabelForSize, getVideoAspectRatioLabel, matchesVideoAspectRatio} from './videoAspectRatios.ts'

export interface PictureUploadingProps {
    isVideo?: boolean
    isAudio?: boolean
    entries?: Map<string, string> | Record<string, string>
    dimensions?: { width: number; height: number }
    videoAspectRatio?: CreativeBean.AspectRatioEnum
    previewSize?: { width: string; height: string }
    onComplete: (entries: Record<string, string>, files: File[]) => void
    listType?: 'picture-card' | 'text' | 'picture' | 'drag'
    maxCount?: number
    disabled?: boolean
    label?: string
}

export interface PictureUploadRef {
    clearFiles: () => void
}

const MAX_PREVIEW_WIDTH = 260
const MAX_PREVIEW_HEIGHT = 260

const ALLOWED_IMAGE_MIME = ['image/png', 'image/jpeg', 'image/jpg', 'image/pjpeg', 'image/gif']
const ALLOWED_IMAGE_EXT = ['.png', '.jpg', '.jpeg', '.gif']

const ALLOWED_AUDIO_MIME = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav', 'audio/ogg', 'audio/aac']
const ALLOWED_AUDIO_EXT = ['.mp3', '.wav', '.ogg', '.aac']

const ALLOWED_VIDEO_MIME = ['video/mp4', 'video/quicktime']
const ALLOWED_VIDEO_EXT = ['.mp4', '.mov']

const normalizeEntries = (entries?: PictureUploadingProps['entries']): Record<string, string> => {
    if (!entries) return {}
    if (entries instanceof Map) {
        const obj: Record<string, string> = {}
        entries.forEach((v, k) => {
            obj[k] = v
        })
        return obj
    }
    const src = entries as Record<string, unknown>
    const filtered: Record<string, string> = {}
    Object.entries(src).forEach(([k, v]) => {
        if (typeof v === 'string') filtered[k] = v
    })
    return filtered
}

const buildInitialList = (entriesObj: Record<string, string>, isVideo?: boolean, isAudio?: boolean): UploadFile[] =>
    Object.entries(entriesObj).map(([key, value]) => ({
        uid: key,
        name: (isVideo ? 'VIDEO-' : isAudio ? 'AUDIO-' : 'IMAGE-') + key,
        status: 'done' as const,
        url: env.staticBucket + '/creative-images' + value,
        size: 0,
        type: '',
    }))

const formatSize = (bytes?: number) => {
    if (!bytes || bytes <= 0) return ''
    if (bytes < 1024) return bytes + ' B'
    const kb = bytes / 1024
    if (kb < 1024) return kb.toFixed(0) + ' KB'
    return (kb / 1024).toFixed(2) + ' MB'
}

const extractServerEntries = (files: UploadFile[]): Record<string, string> => {
    const map: Record<string, string> = {}
    files.forEach((file) => {
        if (file.url) {
            const idx = file.url.indexOf('/creative-images')
            if (idx >= 0) map[file.uid] = file.url.substring(idx + '/creative-images'.length)
        }
    })
    return map
}

const PictureUpload = forwardRef<PictureUploadRef, PictureUploadingProps>((props, ref) => {
    const {isVideo, isAudio, entries, dimensions, videoAspectRatio, previewSize, onComplete, listType, maxCount: maxCountRaw, disabled, label} =
        props

    const appContext = useAppContext()

    const maxCount = typeof maxCountRaw === 'number' ? maxCountRaw : 8
    const singleMode = maxCount === 1

    const [previewVisible, setPreviewVisible] = useState(false)
    const [previewDimensions, setPreviewDimensions] = useState<{ width?: number; height?: number }>({})
    const [previewImage, setPreviewImage] = useState('')
    const [previewFile, setPreviewFile] = useState<UploadFile>({url: undefined} as UploadFile)
    const [fileList, setFileList] = useState<UploadFile[]>([])

    const containerRef = useRef<HTMLDivElement>(null)
    const createdObjectUrlsRef = useRef<Set<string>>(new Set())
    const aliveRef = useRef(true)
    const initializedRef = useRef(false)
    const prevDimsRef = useRef<{ w?: number; h?: number }>({})
    const prevVideoAspectRatioRef = useRef<CreativeBean.AspectRatioEnum | undefined>(undefined)

    useImperativeHandle(
        ref,
        () => ({
            clearFiles() {
                revokeAllObjectUrls()
                setFileList([])
                propagate([])
                initializedRef.current = false
            },
        }),
        [],
    )

    useEffect(() => {
        aliveRef.current = true
        return () => {
            aliveRef.current = false
            revokeAllObjectUrls()
        }
    }, [])

    useEffect(() => {
        if (initializedRef.current) return
        const entriesObj = normalizeEntries(entries)
        if (fileList.length === 0 && Object.keys(entriesObj).length) {
            const initial = buildInitialList(entriesObj, isVideo, isAudio)
            setFileList(initial)
            propagate(initial)
            initializedRef.current = true
        }
    }, [isVideo, isAudio, entries, fileList.length])

    useEffect(() => {
        if (!dimensions) return
        if (isAudio || isVideo) return
        const prev = prevDimsRef.current
        const currW = dimensions.width
        const currH = dimensions.height
        if (prev.w === undefined && prev.h === undefined) {
            prevDimsRef.current = {w: currW, h: currH}
            return
        }
        if (prev.w !== currW || prev.h !== currH) {
            revokeAllObjectUrls()
            setFileList([])
            propagate([])
            initializedRef.current = false
            prevDimsRef.current = {w: currW, h: currH}
        }
    }, [dimensions?.width, dimensions?.height, isVideo, isAudio])

    useEffect(() => {
        if (!isVideo) return
        if (prevVideoAspectRatioRef.current === undefined) {
            prevVideoAspectRatioRef.current = videoAspectRatio
            return
        }
        if (prevVideoAspectRatioRef.current !== videoAspectRatio) {
            revokeAllObjectUrls()
            setFileList([])
            propagate([])
            initializedRef.current = false
            prevVideoAspectRatioRef.current = videoAspectRatio
        }
    }, [isVideo, videoAspectRatio])

    const revokeAllObjectUrls = () => {
        createdObjectUrlsRef.current.forEach((url) => {
            try {
                URL.revokeObjectURL(url)
            } catch {
            }
        })
        createdObjectUrlsRef.current.clear()
    }

    const propagate = (list: UploadFile[]) => {
        const existingEntries = extractServerEntries(list.filter((f) => f.url && f.url.includes('/creative-images')))
        const localFiles = list
            .filter((f) => f.originFileObj && f.url && f.url.startsWith('blob:'))
            .map((f) => f.originFileObj as File)
        onComplete(existingEntries, localFiles)
    }

    const getBase64 = (file: File): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => {
                if (typeof reader.result === 'string') resolve(reader.result)
                else reject(new Error('Unexpected base64 result type'))
            }
            reader.onerror = (err) => reject(err)
            reader.readAsDataURL(file)
        })

    const updatePreviewDimensions = useCallback(
        (url: string) => {
            if (isVideo) {
                const video = document.createElement('video')
                video.src = url
                video.addEventListener(
                    'loadedmetadata',
                    () => {
                        if (!aliveRef.current) return
                        setPreviewDimensions({width: video.videoWidth, height: video.videoHeight})
                    },
                    {once: true},
                )
            } else if (!isAudio) {
                const img = new Image()
                img.src = url
                img.onload = () => {
                    if (!aliveRef.current) return
                    setPreviewDimensions({width: img.width, height: img.height})
                }
            } else {
                setPreviewDimensions({})
            }
        },
        [isVideo, isAudio],
    )

    const handlePreview = useCallback(
        async (file: UploadFile) => {
            if (!file.url && !file.preview && file.originFileObj) {
                try {
                    file.preview = await getBase64(file.originFileObj as File)
                } catch {
                }
            }
            const src = file.url || (typeof file.preview === 'string' ? file.preview : '')
            if (!src) return
            setPreviewImage(src)
            setPreviewFile(file)
            updatePreviewDimensions(src)
            setPreviewVisible(true)
        },
        [updatePreviewDimensions],
    )

    const applySingleModeStyles = useCallback(() => {
        if (!singleMode) return
        const root = containerRef.current
        if (!root) return
        if (fileList.length === 0) {
            const select = root.querySelector('.ant-upload-select-picture-card') as HTMLElement | null
            if (select) {
                select.style.width = '100%'
                select.style.height = previewSize?.height || (dimensions ? dimensions.height + 'px' : isAudio ? '80px' : '250px')
                select.style.margin = '0'
                select.style.display = 'flex'
                select.style.alignItems = 'center'
                select.style.justifyContent = 'center'
                select.style.border = '1px solid #d9d9d9'
                select.style.borderRadius = '6px'
                select.style.background = '#fff'
            }
        } else {
            const containers = root.querySelectorAll(
                '.ant-upload-list-item-container, .ant-upload-list-picture-card-container',
            )
            containers.forEach((el) => {
                const c = el as HTMLElement
                c.style.width = '100%'
                c.style.marginRight = '0'
                const inner = c.querySelector('.custom-uploader') as HTMLElement | null
                if (inner) {
                    inner.style.width = '100%'
                    inner.style.height = 'auto'
                }
            })
        }
    }, [singleMode, fileList.length, previewSize?.height, dimensions, isAudio])

    useLayoutEffect(() => {
        const id = window.setTimeout(applySingleModeStyles, 0)
        return () => window.clearTimeout(id)
    }, [applySingleModeStyles, fileList.length])

    const singleModeItemRender = useCallback(
        (file: UploadFile) => {
            const size = file.originFileObj ? formatSize((file.originFileObj as File).size) : formatSize(file.size)
            const hideName =
                !file.originFileObj &&
                (file.name?.startsWith('IMAGE-') || file.name?.startsWith('VIDEO-') || file.name?.startsWith('AUDIO-'))
            const src = file.url || ''

            return (
                <div
                    className="custom-uploader"
                    style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '24px',
                        padding: '16px 20px 20px 20px',
                        boxSizing: 'border-box',
                        position: 'relative',
                        borderRadius: '6px',
                        border: '1px solid #d9d9d9',
                        background: '#fff',
                    }}
                >
                    <div
                        style={{
                            maxWidth: MAX_PREVIEW_WIDTH,
                            maxHeight: MAX_PREVIEW_HEIGHT,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                            border: '1px solid #e5e5e5',
                            padding: '4px',
                            boxSizing: 'border-box',
                            minWidth: isAudio ? '260px' : '120px',
                            minHeight: isAudio ? '54px' : '90px',
                        }}
                    >
                        {src ? (
                            isVideo ? (
                                <video
                                    src={src}
                                    style={{width: '100%', height: '100%', objectFit: 'contain'}}
                                    onClick={() => handlePreview(file)}
                                />
                            ) : isAudio ? (
                                <audio src={src} controls style={{width: '100%'}}/>
                            ) : (
                                <img
                                    src={src}
                                    alt={hideName ? '' : file.name || 'image'}
                                    style={{width: '100%', height: '100%', objectFit: 'contain'}}
                                    onClick={() => handlePreview(file)}
                                />
                            )
                        ) : (
                            <div style={{fontSize: 12, color: '#999'}}>No preview</div>
                        )}
                    </div>
                    {!hideName && (
                        <div style={{
                            display: 'flex',
                            flex: 1,
                            flexDirection: 'column',
                            minWidth: 0,
                            paddingTop: '4px',
                            paddingRight: '32px',
                        }}>
              <span
                  style={{
                      fontSize: '16px',
                      lineHeight: '20px',
                      color: '#47639c',
                      cursor: 'pointer',
                      wordBreak: 'break-all',
                  }}
                  onClick={() => handlePreview(file)}
              >
                {file.name}
              </span>
                            {size && <span style={{fontSize: '12px', color: '#9e9e9e', marginTop: '6px'}}>{size}</span>}
                        </div>
                    )}
                    <DeleteOutlined
                        onClick={() => handleRemove(file)}
                        style={{
                            position: 'absolute',
                            top: '14px',
                            right: '18px',
                            fontSize: '18px',
                            cursor: 'pointer',
                            color: '#666',
                        }}
                    />
                </div>
            )
        },
        [handlePreview, isVideo, isAudio],
    )

    const validateAndBuildUploadFile = useCallback(
        async (file: File): Promise<UploadFile | null> => {
            if (isVideo) {
                const lowerName = file.name.toLowerCase()
                const hasValidExt = ALLOWED_VIDEO_EXT.some((ext) => lowerName.endsWith(ext))
                const hasValidMime = !file.type || ALLOWED_VIDEO_MIME.includes(file.type) || hasValidExt
                if (!hasValidMime) {
                    showError(appContext, 'Unsupported video format (allowed: MP4, MOV)')
                    return null
                }
                if (!videoAspectRatio) {
                    showError(appContext, 'Please select video aspect ratio first')
                    return null
                }
                const objectUrl = URL.createObjectURL(file)
                const passed = await new Promise<{ ok: boolean; w?: number; h?: number }>((resolve) => {
                    const v = document.createElement('video')
                    v.preload = 'metadata'
                    v.src = objectUrl
                    v.onloadedmetadata = () => {
                        const w = v.videoWidth
                        const h = v.videoHeight
                        if (!matchesVideoAspectRatio(videoAspectRatio, w, h)) {
                            const ratioLabel = getVideoAspectRatioLabel(videoAspectRatio)
                            const uploadedRatioLabel = getAspectRatioLabelForSize(w, h)
                            showError(
                                appContext,
                                `Video must match aspect ratio ${ratioLabel}. Uploaded: ${w}x${h}px (${uploadedRatioLabel})`
                            )
                            resolve({ok: false})
                        } else {
                            resolve({ok: true, w, h})
                        }
                    }
                    v.onerror = () => {
                        showError(appContext, 'Unable to read video file')
                        resolve({ok: false})
                    }
                })
                if (!passed.ok) {
                    try {
                        URL.revokeObjectURL(objectUrl)
                    } catch {
                    }
                    return null
                }
                createdObjectUrlsRef.current.add(objectUrl)
                return {
                    uid: crypto.randomUUID(),
                    name: file.name,
                    status: 'done',
                    // @ts-ignore
                    originFileObj: file,
                    url: objectUrl,
                    size: file.size,
                    type: file.type,
                }
            }

            if (isAudio) {
                const lowerName = file.name.toLowerCase()
                const hasValidExt = ALLOWED_AUDIO_EXT.some((ext) => lowerName.endsWith(ext))
                const hasValidMime = !file.type || ALLOWED_AUDIO_MIME.includes(file.type) || hasValidExt
                if (!hasValidMime) {
                    showError(appContext, 'Unsupported audio format (allowed: mp3, wav, ogg, aac)')
                    return null
                }
                const url = URL.createObjectURL(file)
                createdObjectUrlsRef.current.add(url)
                return {
                    uid: crypto.randomUUID(),
                    name: file.name,
                    status: 'done',
                    // @ts-ignore
                    originFileObj: file,
                    url,
                    size: file.size,
                    type: file.type,
                }
            }

            // Images
            const lowerName = file.name.toLowerCase()
            const hasValidExt = ALLOWED_IMAGE_EXT.some((ext) => lowerName.endsWith(ext))
            if ((!file.type || !ALLOWED_IMAGE_MIME.includes(file.type)) && !hasValidExt) {
                showError(appContext, 'Only PNG or JPG images are allowed')
                return null
            }

            if (!dimensions) {
                const url = URL.createObjectURL(file)
                createdObjectUrlsRef.current.add(url)
                return {
                    uid: crypto.randomUUID(),
                    name: file.name,
                    status: 'done',
                    // @ts-ignore
                    originFileObj: file,
                    url,
                    size: file.size,
                    type: file.type,
                }
            }

            const blobUrl = URL.createObjectURL(file)
            try {
                const img = await new Promise<HTMLImageElement>((resolve, reject) => {
                    const i = new Image()
                    i.onload = () => resolve(i)
                    i.onerror = reject
                    i.src = blobUrl
                })
                const {width: w, height: h} = img
                if (w !== dimensions.width || h !== dimensions.height) {
                    showError(
                        appContext,
                        `Image must be exactly ${dimensions.width}x${dimensions.height}px. Uploaded: ${w}x${h}px`,
                    )
                    URL.revokeObjectURL(blobUrl)
                    return null
                }
                createdObjectUrlsRef.current.add(blobUrl)
                return {
                    uid: crypto.randomUUID(),
                    name: file.name,
                    status: 'done',
                    // @ts-ignore
                    originFileObj: file,
                    url: blobUrl,
                    size: file.size,
                    type: file.type,
                }
            } catch {
                showError(appContext, 'Unable to read image file')
                URL.revokeObjectURL(blobUrl)
                return null
            }
        },
        [dimensions, isVideo, isAudio, appContext, videoAspectRatio],
    )

    const handleBeforeUpload = useCallback(
        async (rawFile: File) => {
            if (disabled) return false

            const uploadFile = await validateAndBuildUploadFile(rawFile)
            if (!uploadFile) return false

            setFileList((prev) => {
                const next = singleMode ? [uploadFile] : [...prev, uploadFile]
                while (next.length > maxCount) {
                    const removed = next.shift()
                    if (removed && removed.url && removed.url.startsWith('blob:')) {
                        try {
                            URL.revokeObjectURL(removed.url)
                        } catch {
                        }
                        createdObjectUrlsRef.current.delete(removed.url)
                    }
                }
                propagate(next)
                return next
            })

            return false
        },
        [disabled, validateAndBuildUploadFile, singleMode, maxCount],
    )

    const handleRemove = useCallback((file: UploadFile) => {
        setFileList((prev) => {
            const next = prev.filter((f) => f.uid !== file.uid)
            if (file.url && file.url.startsWith('blob:')) {
                try {
                    URL.revokeObjectURL(file.url)
                } catch {
                }
                createdObjectUrlsRef.current.delete(file.url)
            }
            propagate(next)
            return next
        })
    }, [])

    const uploadTrigger = (
        <div
            style={{
                width: '100%',
                height: previewSize?.height || (dimensions ? dimensions.height + 'px' : isAudio ? '80px' : '250px'),
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <PlusOutlined/>
            <div className="ant-upload-text">{label || 'Upload'}</div>
        </div>
    )

    return (
        <div ref={containerRef}>
            <Upload
                accept={
                    isVideo
                        ? '.mp4,.mov,video/mp4,video/quicktime'
                        : isAudio
                            ? 'audio/*'
                            : 'image/png,image/jpeg,image/gif'
                }
                multiple={!singleMode}
                fileList={fileList}
                customRequest={({onSuccess}) => {
                    onSuccess && onSuccess('ok')
                }}
                listType={(listType || 'picture-card') as any}
                onPreview={handlePreview}
                beforeUpload={handleBeforeUpload}
                disabled={disabled}
                itemRender={(originNode, file) => (singleMode ? singleModeItemRender(file as UploadFile) : originNode)}
                style={
                    singleMode
                        ? fileList.length === 0
                            ? {
                                width: '100%',
                                height: previewSize?.height || (dimensions ? dimensions.height + 'px' : isAudio ? '80px' : '250px'),
                            }
                            : {width: '100%'}
                        : undefined
                }
                onRemove={(file) => {
                    handleRemove(file as UploadFile)
                    return false
                }}
            >
                {singleMode ? (
                    fileList.length === 0 ? (
                        uploadTrigger
                    ) : null
                ) : fileList.length >= maxCount ? null : (
                    <PlusOutlined/>
                )}
            </Upload>

            <Modal
                open={previewVisible}
                footer={null}
                onCancel={() => setPreviewVisible(false)}
                style={{maxWidth: '600px'}}
            >
                {!isAudio && (
                    <p style={{textAlign: 'center'}}>
                        {previewDimensions.width} {previewDimensions.width ? 'x' : ''} {previewDimensions.height}
                    </p>
                )}
                {isVideo ? (
                    previewVisible && <video style={{maxWidth: '100%'}} src={previewFile.url} autoPlay controls/>
                ) : isAudio ? (
                    previewVisible && <audio style={{width: '100%'}} src={previewFile.url} autoPlay controls/>
                ) : (
                    <img alt="asset" style={{width: '100%', height: '100%', objectFit: 'contain'}} src={previewImage}/>
                )}
            </Modal>
        </div>
    )
})

export default PictureUpload
